<template>
  <TsTag
      v-if="tagLabel"
      variant="info"
      @click="openBranchSelector"
      rounded
      :append-class="twJoin(tagBaseClasses, allowBranchSwitch && 'cursor-pointer')"
  >
    <TsIcon name="ep:location" class="shrink-0" size="20" />
    <TsTypography
        weight="semibold"
        append-class="md:text-xs text-primary"
        data-testid="header-branch-text"
        as="span"
    >
      {{
        branchStore.lastSavedBranch?.name
            ? branchStore.lastSavedBranch.name
            : useTranslation("selectNearBranch", "Selecteer een vestiging")
      }}
    </TsTypography>
  </TsTag>

  <TsModalBranchSelector
      v-if="rootStore.isAppHydrated && allowBranchSwitch"
      v-model:visible="branchStore.branchSelectorModalVisible"
      id="header-branch-selector"
      data-testid="header-branch-selector"
  />
</template>
<script setup lang="ts">
import { useTranslation } from "~/composables/useTranslation";
import { onBeforeUnmount, ref, watch } from 'vue';
import { twJoin } from "tailwind-merge";

// Use defineAsyncComponent with error handling and cleanup
const TsModalBranchSelector = defineAsyncComponent({
  loader: () => import('~/components/organisms/TsModalBranchSelector.vue'),
  onError(error, retry, fail, attempts) {
    console.error('Error loading TsModalBranchSelector:', error);
    if (attempts <= 3) {
      retry();
    } else {
      fail();
    }
  }
});

// Store component instance for proper cleanup
const modalInstance = ref(null);

const route = useRoute();
const branchStore = useBranchStore();
const rootStore = useRootStore();

// Use refs instead of computed properties to avoid memory leaks
const isBranchListingPage = ref(false);
const allowBranchSwitch = ref(true);
const tagLabel = ref('');

// Update the refs based on the current state
function updateRefs() {
  isBranchListingPage.value = /^\/branches(?:\?.*)?$/.test(route.path);

  allowBranchSwitch.value = isBranchListingPage.value
      ? Boolean(branchStore.lastSavedBranch?.name)
      : true;

  const branchName = branchStore.lastSavedBranch?.name;

  if (isBranchListingPage.value) {
    tagLabel.value = branchName || ""; // To not show branch selector if branch is not selected and on the listing page
  } else {
    tagLabel.value = branchName || useTranslation("selectNearBranch", "Selecteer een vestiging");
  }
}

// Watch for changes in route and branch store
const stopWatchers = [
  watch(() => route.path, updateRefs, { immediate: true }),
  watch(() => branchStore.lastSavedBranch?.name, updateRefs, { immediate: true })
];

const tagBaseClasses = "select-none hover:bg-info/20 transition-colors font-semibold text-primary max-md:bg-transparent bg-[#065CAB1A] max-md:p-0";

function openBranchSelector() {
  if (isBranchListingPage.value) return;
  branchStore.branchSelectorModalVisible = true;
}

// Clean up resources when component is unmounted
onBeforeUnmount(() => {
  // Stop all watchers
  stopWatchers.forEach(stop => stop());

  // Clear component instance
  modalInstance.value = null;
});
</script>
